import React from 'react';
import { Box, Link, Typography, alpha, useTheme } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * Breadcrumbs that double as drop targets so users can drag items "up" a level
 * by dropping them on any ancestor in the trail.
 *
 * Visual style: chip-like pills with the active segment filled. Plays nicely
 * with the dashboard-level breadcrumb directly above (different sizing/weight).
 */
export default function Breadcrumbs({
  breadcrumbs,
  onNavigate,
  onDropOnCrumb,
  dropTargetPath,
  setDropTargetPath,
}) {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32, flexWrap: 'wrap', gap: 0.25 }}>
      {breadcrumbs.map((crumb, idx) => {
        const isLast = idx === breadcrumbs.length - 1;
        const isDropTarget = dropTargetPath === crumb.logicalPath;
        const pillSx = {
          display: 'inline-flex', alignItems: 'center', gap: 0.5,
          px: 1, py: 0.4, borderRadius: 1.5,
          fontSize: 13, fontWeight: isLast ? 600 : 500,
          border: '1px solid transparent',
          color: isLast ? 'text.primary' : 'text.secondary',
          bgcolor: isLast ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
          ...(isDropTarget && {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.18),
          }),
        };
        const content = (
          <>
            {crumb.isRoot && <HomeIcon sx={{ fontSize: 15 }} />}
            <span>{crumb.name}</span>
          </>
        );
        return (
          <React.Fragment key={crumb.logicalPath}>
            {isLast ? (
              <Box sx={pillSx}>{content}</Box>
            ) : (
              <Link
                component="button" type="button" underline="none"
                onClick={() => onNavigate(crumb.logicalPath)}
                onDragOver={(e) => {
                  e.preventDefault(); e.dataTransfer.dropEffect = 'move';
                  setDropTargetPath(crumb.logicalPath);
                }}
                onDragLeave={() => setDropTargetPath(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDropTargetPath(null);
                  onDropOnCrumb?.(crumb.logicalPath, e);
                }}
                sx={{
                  ...pillSx, cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.primary' },
                }}
              >
                {content}
              </Link>
            )}
            {!isLast && (
              <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled', mx: 0.1 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
